import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { A11yModule } from '@angular/cdk/a11y';

import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { ClickOutsideModule } from 'ng-click-outside';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { PopoverModule } from 'primeng/popover';
import { TableModule } from 'primeng/table';

import { TargetingSearchComponent } from './components/TargetingSearch/targeting-search.component';
import { LayoutModule } from '../../Common/Layout/layout.module';
import { LocalizationModule } from '../../Core/Localization/localization.module';
import { CommonFormsModule } from '../../Common/Forms/forms.module';
import { MiscModule } from '../../Common/Misc/misc.module';
import { TakeOverModule } from '../../Common/TakeOver/take-over.module';
import { TargetingResultsComponent } from './components/TargetingResults/targeting-results.component';
import { TargetingSearchesService } from './services/targeting-searches.service';
import { TargetingSearchService } from './services/targeting-search.service';
import { FilterModule } from '../../Common/Filter/filter.module';
import { TagsModule } from '../../Common/Tags/tags.module';
import { ContactSearchComponent } from './components/TargetingSearch/ContactSearch/contact-search.component';
import { OutletSearchComponent } from './components/TargetingSearch/OutletSearch/outlet-search.component';
import { CommonBasicModule } from '../../Common/Basic/basic.module';
import { ContactDetailsExtendedComponent } from './components/TargetingResults/ContactDetailsExtended/contact-details-extended.component';
import { CommonDialogsModule } from '../../Common/Dialogs/common-dialogs.module';
import { ContactService } from '../../Common/TargetingShared/services/contact-service';
import { MonitorSharedModule } from '../../Monitor/Shared/monitoring-shared.module';
import { JournodexModule } from '../../Common/Journodex/journodex.module';
import { DistributionComponent } from './components/Distribution/distribution.component';
import { RecipientEditorComponent } from './components/Distribution/RecipientEditor/recipient-editor.component';
import { CoreDialogsModule } from '../../Core/Dialogs/dialogs.module';
import { ContactCsvImportDialogService } from './services/contact-csv-import-dialog.service';
import { ManageListDialogComponent } from './components/TargetingResults/ManageListDialog/manage-list-dialog.component';
import { StatusFilterComponent } from './components/TargetingResults/StatusFilter/status-filter.component';
import { ContactImporterComponent } from './components/ContactImporter/contact-importer.component';
import { DataSelectDialogComponent } from './../../Common/Dialogs/components/DataSelectDialog/data-select-dialog.component';
import { ChartsModule } from '../../Common/Charts/charts.module';
import { DistributionReportComponent } from './components/Distribution/DistributionReport/distribution.report.component';
import { DistributionDashboardComponent } from './components/Distribution/Dashboard/distribution.dashboard.component';
import { RecipientSearchService } from './components/Distribution/DistributionReport/RecipientSearch/recipient-search.service';
import { RecipientSearchTabComponent } from './components/Distribution/DistributionReport/RecipientSearch/recipient-search-tab.component';
import { RecipientCardComponent } from './components/Distribution/DistributionReport/RecipientSearch/RecipientCard/recipient-card.component';
import { PageBuilderModule } from '../../Common/PageBuilder/page-builder.module';
import { DistributionHeaderComponent } from './components/Distribution/DistributionHeader/distribution.header.component';
import { CsvExportDialogComponent } from './components/TargetingResults/CsvExportDialog/csv-export-dialog.component';
import { DistributionConfirmationComponent } from './components/Distribution/DistributionConfirmation/distribution-confirmation.component';
import { RecipientPanelComponent } from './components/Distribution/RecipientPanel/recipient-panel.component';
import { QuotaDropdownComponent } from './components/Distribution/QuotaDropdown/quota-dropdown.component';
import { DevicePreviewDialogComponent } from './components/Distribution/DevicePreviewDialog/device-preview-dialog.component';
import { InfluencerStreamsComponent } from './components/InfluencerStreams/influencer-streams.component';
import { InfluencerStreamComponent } from './components/InfluencerStreams/InfluencerStream/influencer-stream.component';
import { FooterFieldComponent } from './components/Distribution/FooterField/footer-field.component';
import { TargetingRhpDialogService } from '../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import {
    DistributionGlobalCustomizationComponent
} from './components/Distribution/DistributionGlobalCustomization/distribution-global-customization.component';
import { StatusFilterService } from './components/TargetingResults/StatusFilter/status-filter.service';
import { AdditionalRecipientSearchService } from './components/Distribution/DistributionReport/RecipientSearch/additional-recipient-search.service';
import {
    AdditionalRecipientToolbarComponent
} from './components/Distribution/DistributionReport/RecipientSearch/AdditionalRecipientToolbar/additional-recipient-toolbar.component';
import { NewDistributionComponent } from './components/Distribution/NewDistribution/new-distribution.component';
import { StreamContentCheckboxFilterComponent } from './components/TargetingResults/StreamContentCheckboxFilter/stream-content-checkbox-filter.component';
import { DistributionService } from '../../Common/TargetingShared/services/distribution.service';
import { AddPrivateDataDialogComponent } from './components/TargetingResults/AddPrivateDataDialog/add-private-data-dialog.component';
import { TargetingRhpContextsService } from '../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { TargetingSharedModule } from '../../Common/TargetingShared/targeting-shared.module';
import { PlainTextEditorDialogComponent } from './components/Distribution/PlainTextEditorDialog/plain-text-editor-dialog.component';
import { DistributionPlainTextService } from './components/Distribution/PlainTextEditorDialog/plain-text.service';
import { JournodexEntityFactory } from './factories/journodex-entity.factory';
import { TargetingResultFactory } from './factories/targeting-result.factory';
import { EmploymentFactory } from './factories/employment.factory';
import { IncludeExcludeSubjectChipsComponent } from './components/IncludeExcludeSubjectChips/include-exclude-subject-chips.component';
import { OnlineBoostConfirmationDialogComponent } from './components/Distribution/OnlineBoostConfirmationDialog/online-boost-confirmation-dialog.component';
import { DistributionViewInBrowserComponent } from './components/Distribution/ViewInBrowserDistribution/distribution-view-in-browser.component';
import { PortfolioEditorComponent } from './components/TargetingResults/PortfolioEditor/portfolio-editor.component';
import { RecipientViewerComponent } from './components/Distribution/RecipientEditor/RecipientViewer/recipient-viewer.component';
import { RecipientViewerCardComponent } from './components/Distribution/RecipientEditor/RecipientViewer/RecipientViewerCard/recipient-viewer-card.component';
import { PortfolioHypeComponent } from './components/Portfolios/PortfolioHype/portfolio-hype.component';
import { PortfolioService } from './components/Portfolios/portfolio-service';
import { PortfolioLeftPanelComponent } from './components/Portfolios/PortfolioLeftPanel/portfolio-left-panel.component';
import { MentionsModule } from '../../Common/Mentions/mentions.module';
import { RhpContextsModule } from '../../Common/RhpContexts/rhp-contexts.module';
import { PostDistributionAmplifyDialogComponent } from './components/Distribution/PostDistributionAmplifyDialog/post-distribution-amplify-dialog.component';
import { AIDistributionHeaderDialogComponent } from './components/Distribution/AISubjectLineDialog/AI-distribution-header-dialog.component';
import { DistributionSaveTemplateDialogComponent } from './components/Distribution/DistributionSaveTemplateDialog/distribution-save-template-dialog.component';
import { AIQuickDistributionDialogComponent } from './components/Distribution/AIQuickDistributionDialog/ai-quick-distribution-dialog.component';
import { AbstractAIHelperDialogService } from '../../Common/Dialogs/services/abstract-AI-helper-dialog.service';
import { DistributionAIHelperDialogService } from './services/distribution-AI-helper-dialog.service';
import { DistributionStepsNavigationComponent } from './components/Distribution/DistributionStepsNavigation/distribution-steps-navigation.component';
import { DistributionReviewComponent } from './components/Distribution/DistributionReview/distribution-review.component';
import { PersonalizeComponent } from './components/Distribution/Personalize/personalize.component';
import { RecommendedRecipientPanelComponent } from './components/Distribution/RecipientEditor/RecommendedRecipient/recommended-recipient-panel.component';
import { HeaderEditorService } from '../../Common/TargetingShared/services/header-editor.service';
import { RecommendedRecipientService } from './components/Distribution/RecipientEditor/RecommendedRecipient/recommended-recipient-service';
import { RecipientEditorService } from './components/Distribution/RecipientEditor/recipient-editor.service';
import { ManageDistDialogComponent } from './components/TargetingResults/ManageDistDialog/manage-dist-dialog.component';
import { PersonalizationDetailCardComponent } from './components/Distribution/PersonalizationDetailCard/personalization-detail-card.component';
import { RecipientPersonalizationDialogComponent } from './components/Distribution/RecipientPersonalizationDialog/recipient-personalization-dialog.component';
import { PersonalizationWorkbenchDialogComponent } from './components/Distribution/PersonalizationWorkbenchDialog/personalization-workbench-dialog.component';
import { PersonalizationService } from './services/personalization.service';
import { PersonalizationTabComponent } from './components/Distribution/PersonalizationTab/personalization-tab.component';
import { LockSearchDialogComponent } from './components/Distribution/LockSearchDialog/lock-search-dialog.component';
import { QuickEditPanelComponent } from './components/Distribution/RecipientEditor/QuickEditPanel/quick-edit-panel.component';
import { QuickEditSearchComponent } from './components/Distribution/RecipientEditor/QuickEditSearch/quick-edit-search.component';
// eslint-disable-next-line max-len
import { QuickEditRefinementsSearchComponent } from './components/Distribution/RecipientEditor/QuickEditRefinementsSearch/quick-edit-refinements-search.component';
import { DeleteContainerComponent } from './components/Distribution/RecipientEditor/DeleteContainer/delete-container.component';
import { SubjectDialogComponent } from './components/TargetingSearch/SubjectDialog/subject-dialog.component';
import { EmailConversationService } from './services/email-conversation.service';
import { RemoveFromListsDialogComponent } from './components/Distribution/RemoveFromListsDialog/remove-from-lists-dialog.component';
import { RemoveFromListsConfirmDialogComponent } from './components/Distribution/RemoveFromListsConfirmDialog/remove-from-lists-confirm-dialog.component';
import { NegativeRecipientSearchService } from './components/Distribution/RemoveFromListsDialog/negative-recipient-search.service';

import { CsvInteractionExportDialogComponent } from './components/TargetingResults/CsvInteractionExportDialog/csv-interaction-export-dialog.component';
import { TargetingSearchesApiService } from '../../api/targeting/targeting-searches.api.service';
import { SourcePickerService } from './services/source-picker-service';
import { IncludeExcludeMediaMarketChipsComponent } from './components/IncludeExcludeMediaMarketChips/include-exclude-media-market-chips.component';
import { MediaMarketDialogComponent } from './components/TargetingSearch/MediaMarketDialog/media-market-dialog.component';
import { AEORecommendationsDialogComponent } from './components/Distribution/AEORecommendationsDialog/aeo-recommendations-dialog.component';

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        LayoutModule,
        CommonBasicModule,
        LocalizationModule,
        CommonFormsModule,
        CommonDialogsModule,
        AutoCompleteModule,
        MiscModule,
        TooltipModule,
        PopoverModule,
        TableModule,
        SelectModule,
        InputTextModule,
        FilterModule,
        JournodexModule,
        CoreDialogsModule,
        DatePickerModule,
        ChartsModule,
        ProgressBarModule,
        PageBuilderModule,
        TagsModule,
        MonitorSharedModule,
        CodemirrorModule,
        DragDropModule,
        ClickOutsideModule,
        TargetingSharedModule,
        MentionsModule,
        RhpContextsModule,
        A11yModule,
        TakeOverModule,
    ],
    declarations: [
        TargetingSearchComponent,
        ContactSearchComponent,
        OutletSearchComponent,
        TargetingResultsComponent,
        ContactDetailsExtendedComponent,
        DistributionComponent,
        RecipientEditorComponent,
        RecipientViewerComponent,
        ManageListDialogComponent,
        AddPrivateDataDialogComponent,
        OnlineBoostConfirmationDialogComponent,
        StatusFilterComponent,
        StreamContentCheckboxFilterComponent,
        PortfolioEditorComponent,
        ContactImporterComponent,
        DistributionReportComponent,
        NewDistributionComponent,
        DistributionDashboardComponent,
        RecipientSearchTabComponent,
        AdditionalRecipientToolbarComponent,
        RecipientCardComponent,
        DistributionHeaderComponent,
        CsvExportDialogComponent,
        DistributionConfirmationComponent,
        PlainTextEditorDialogComponent,
        RecipientPanelComponent,
        QuotaDropdownComponent,
        DevicePreviewDialogComponent,
        DistributionGlobalCustomizationComponent,
        InfluencerStreamsComponent,
        InfluencerStreamComponent,
        FooterFieldComponent,
        IncludeExcludeSubjectChipsComponent,
        DistributionViewInBrowserComponent,
        RecipientViewerCardComponent,
        PortfolioHypeComponent,
        PortfolioLeftPanelComponent,
        PostDistributionAmplifyDialogComponent,
        AIDistributionHeaderDialogComponent,
        DistributionSaveTemplateDialogComponent,
        AIQuickDistributionDialogComponent,
        DistributionStepsNavigationComponent,
        DistributionReviewComponent,
        RecommendedRecipientPanelComponent,
        ManageDistDialogComponent,
        PersonalizeComponent,
        PersonalizationDetailCardComponent,
        RecommendedRecipientPanelComponent,
        RecipientPersonalizationDialogComponent,
        PersonalizationWorkbenchDialogComponent,
        PersonalizationTabComponent,
        LockSearchDialogComponent,
        QuickEditPanelComponent,
        QuickEditSearchComponent,
        QuickEditRefinementsSearchComponent,
        DeleteContainerComponent,
        SubjectDialogComponent,
        RemoveFromListsDialogComponent,
        RemoveFromListsConfirmDialogComponent,
        CsvInteractionExportDialogComponent,
        IncludeExcludeMediaMarketChipsComponent,
        MediaMarketDialogComponent,
        AEORecommendationsDialogComponent,
    ],
    exports: [
        TargetingSearchComponent,
        ContactSearchComponent,
        OutletSearchComponent,
        TargetingResultsComponent,
        ContactDetailsExtendedComponent,
        ContactImporterComponent,
        DataSelectDialogComponent,
        FooterFieldComponent,
    ],
    providers: [
        TargetingSearchService,
        TargetingSearchesService,
        {
            provide: 'TargetingSearchesService',
            useExisting: TargetingSearchesService
        },
        TargetingSearchesApiService,
        {
            provide: AbstractAIHelperDialogService,
            useClass: DistributionAIHelperDialogService
        },
        ContactService,
        RecipientSearchService,
        ContactCsvImportDialogService,
        TargetingRhpContextsService,
        TargetingRhpDialogService,
        {
            provide: 'ProfileTakeOverInterface',
            useExisting: TargetingRhpDialogService
        },
        StatusFilterService,
        AdditionalRecipientSearchService,
        DistributionService,
        SourcePickerService,
        {
            provide: 'SourcePickerService',
            useExisting: SourcePickerService
        },
        {
            provide: 'DistributionService',
            useExisting: DistributionService
        },
        DistributionPlainTextService,
        JournodexEntityFactory,
        TargetingResultFactory,
        EmploymentFactory,
        PortfolioService,
        HeaderEditorService,
        RecommendedRecipientService,
        RecipientEditorService,
        PersonalizationService,
        {
            provide: 'PersonalizationServiceInterface',
            useExisting: PersonalizationService
        },
        EmailConversationService,
        NegativeRecipientSearchService,
    ]
})
export class DiscoverTargetingModule { }
